import './AvailabilityCalendar.css';

import React, { useEffect, useMemo, useState } from 'react';

import { InPersonIcon, VideoVisitIcon } from './VisitModeIcons';
import { OpenSchedule } from '../types/provider.types';

/** A bookable visit type within a slot. */
export interface SlotVisitType {
  id: string;
  name: string;
  accepts?: 'New' | 'Existing';
  video: boolean;
  /** Patient-facing reason phrases. Not supplied by the ProviderValidator API. */
  reasons?: string[];
}

/** A unique physical appointment slot (one time + department). */
export interface AvailabilityUniqueSlot {
  time: string;
  date: string;
  dayString: string;
  timeString: string;
  timeOfDay: string;
  acceptsNew: boolean;
  acceptsExisting: boolean;
  hasVideo: boolean;
  hasInPerson: boolean;
  departmentId?: string;
  departmentName?: string;
  visitTypeIds: string[];
  visitTypes: SlotVisitType[];
}

interface AvailabilityCalendarProps {
  slots: AvailabilityUniqueSlot[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const TIME_OF_DAY_ORDER = ['Morning', 'Afternoon', 'Evening'];
/** Above this count we split a day's slots into Morning / Afternoon / Evening groups. */
const CATEGORIZE_THRESHOLD = 8;

/** "YYYY-MM-DD" for a given year/month(0-based)/day, without timezone drift. */
function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Morning / Afternoon / Evening from the hour in an "…THH:mm:ss" string. */
function timeOfDayFor(isoLocal: string): string {
  const hour = Number(isoLocal.slice(11, 13));
  if (!Number.isFinite(hour)) return 'Other';
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
}

/**
 * Flattens a location's OpenSchedules into calendar slots.
 *
 * The API emits one entry per visit type per time, so entries sharing a
 * department + time are collapsed into a single slot carrying every visit type.
 * Dates come from the slot's own time string rather than a parsed Date, so a
 * late-evening slot never drifts into the next day.
 */
export function buildAvailabilitySlots(
  schedule: OpenSchedule | undefined | null,
  locationName?: string,
): AvailabilityUniqueSlot[] {
  if (!schedule) return [];
  const dates = schedule.DateAndTimes || schedule.dateAndTimes || [];
  const byKey = new Map<string, AvailabilityUniqueSlot>();

  dates.forEach((dateEntry) => {
    const dayString = dateEntry.DayString || dateEntry.dayString || '';
    const times = dateEntry.Times || dateEntry.times || [];

    times.forEach((slot) => {
      const time = slot.Time || slot.time;
      if (!time) return;

      const departmentId = slot.DepartmentId || slot.departmentId;
      const key = `${departmentId || ''}|${time}`;
      const accepts = slot.Accepts || slot.accepts || '';
      const isExisting = accepts.toLowerCase() === 'existing';
      const visitMode = (slot.VisitType || slot.visitType || '').toLowerCase();
      const isVideo = visitMode.indexOf('video') !== -1;
      const isInPerson = visitMode.indexOf('person') !== -1;

      let entry = byKey.get(key);
      if (!entry) {
        entry = {
          time,
          date: time.slice(0, 10),
          dayString,
          timeString: slot.TimeString || slot.timeString || '',
          timeOfDay: timeOfDayFor(time),
          acceptsNew: false,
          acceptsExisting: false,
          hasVideo: false,
          hasInPerson: false,
          departmentId,
          departmentName: locationName,
          visitTypeIds: [],
          visitTypes: [],
        };
        byKey.set(key, entry);
      }

      if (isExisting) entry.acceptsExisting = true;
      else entry.acceptsNew = true;
      if (isVideo) entry.hasVideo = true;
      if (isInPerson) entry.hasInPerson = true;

      const visitTypeId = slot.VisitTypeId || slot.visitTypeId;
      const visitTypeName = slot.VisitTypeDisplayName || slot.visitTypeDisplayName || '';
      if (visitTypeId && entry.visitTypeIds.indexOf(visitTypeId) === -1) {
        entry.visitTypeIds.push(visitTypeId);
        entry.visitTypes.push({
          id: visitTypeId,
          name: visitTypeName,
          accepts: isExisting ? 'Existing' : 'New',
          video: isVideo,
        });
      }
    });
  });

  const out: AvailabilityUniqueSlot[] = [];
  byKey.forEach((slot) => out.push(slot));
  out.sort((a, b) => a.time.localeCompare(b.time));
  return out;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({ slots }) => {
  // Group slots by local date.
  const slotsByDate = useMemo(() => {
    const map = new Map<string, AvailabilityUniqueSlot[]>();
    slots.forEach((s) => {
      const list = map.get(s.date);
      if (list) list.push(s);
      else map.set(s.date, [s]);
    });
    map.forEach((list) => list.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [slots]);

  const sortedDates = useMemo(() => {
    const keys: string[] = [];
    slotsByDate.forEach((_list, date) => keys.push(date));
    return keys.sort();
  }, [slotsByDate]);
  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];

  const [view, setView] = useState(() => {
    const d = firstDate ? new Date(`${firstDate}T00:00:00`) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(firstDate ?? null);
  // Which slot's visit-type detail panel is expanded (key = department|time).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const slotKey = (s: AvailabilityUniqueSlot) => `${s.departmentId ?? ''}|${s.time}`;

  // Collapse any open visit-type panel when the selected day changes.
  useEffect(() => {
    setExpandedKey(null);
  }, [selectedDate]);

  // Jump to the first available date whenever the current selection is no longer
  // valid. We never override a still-valid manual selection.
  useEffect(() => {
    if (!firstDate) return;
    if (selectedDate && slotsByDate.has(selectedDate)) return;
    setSelectedDate(firstDate);
    const d = new Date(`${firstDate}T00:00:00`);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  }, [firstDate, slotsByDate, selectedDate]);

  const minMonth = firstDate ? firstDate.slice(0, 7) : null;
  const maxMonth = lastDate ? lastDate.slice(0, 7) : null;
  const viewKey = `${view.year}-${String(view.month + 1).padStart(2, '0')}`;
  const canPrev = minMonth !== null && viewKey > minMonth;
  const canNext = maxMonth !== null && viewKey < maxMonth;

  const shiftMonth = (delta: number) => {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const jumpToFirst = () => {
    if (!firstDate) return;
    setSelectedDate(firstDate);
    const d = new Date(`${firstDate}T00:00:00`);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  // Build the day grid for the current view month.
  const cells = useMemo(() => {
    const firstOfMonth = new Date(view.year, view.month, 1);
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const leading = firstOfMonth.getDay();
    const out: Array<{ day: number; date: string } | null> = [];
    for (let i = 0; i < leading; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push({ day: d, date: ymd(view.year, view.month, d) });
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const todayStr = ymd(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const selectedSlots = useMemo(
    () => (selectedDate ? slotsByDate.get(selectedDate) ?? [] : []),
    [selectedDate, slotsByDate],
  );
  const dayDeptCount = useMemo(
    () => new Set(selectedSlots.map((s) => s.departmentName || 'Location')).size,
    [selectedSlots],
  );

  // Categorize the selected day's slots into Morning / Afternoon / Evening once a
  // day gets busy; otherwise keep a single flat list.
  const timeOfDayGroups = useMemo(() => {
    if (selectedSlots.length < CATEGORIZE_THRESHOLD) {
      return [{ label: '', slots: selectedSlots }];
    }
    const buckets = new Map<string, AvailabilityUniqueSlot[]>();
    selectedSlots.forEach((s) => {
      const key = s.timeOfDay || 'Other';
      const list = buckets.get(key);
      if (list) list.push(s);
      else buckets.set(key, [s]);
    });
    const ordered: Array<{ label: string; slots: AvailabilityUniqueSlot[] }> = [];
    TIME_OF_DAY_ORDER.forEach((key) => {
      const list = buckets.get(key);
      if (list) ordered.push({ label: key, slots: list });
    });
    buckets.forEach((list, key) => {
      if (TIME_OF_DAY_ORDER.indexOf(key) === -1) ordered.push({ label: key, slots: list });
    });
    return ordered;
  }, [selectedSlots]);

  if (slots.length === 0) {
    return <div className="cal-empty">No upcoming availability.</div>;
  }

  const totalDays = sortedDates.length;
  const currentMonthKey = ymd(new Date().getFullYear(), new Date().getMonth(), 1).slice(0, 7);
  const jumpedForward = !!minMonth && minMonth > currentMonthKey;
  const firstDateLabel = firstDate
    ? new Date(`${firstDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : '';

  const renderSlotPill = (slot: AvailabilityUniqueSlot, idx: number) => {
    const existingOnly = slot.acceptsExisting && !slot.acceptsNew;
    const key = slotKey(slot);
    const isExpanded = expandedKey === key;
    const classes = [
      'cal-slot',
      existingOnly ? 'cal-slot-existing' : 'cal-slot-new',
      isExpanded ? 'cal-slot-open' : '',
    ].filter(Boolean).join(' ');
    const vtNames = slot.visitTypes.map((v) => v.name).filter(Boolean);
    const title = vtNames.length
      ? `${vtNames.length} visit type${vtNames.length === 1 ? '' : 's'}: ${vtNames.join(', ')}`
      : `${slot.departmentName || ''}${slot.departmentName ? ' — ' : ''}${existingOnly ? 'Existing patients only' : 'Accepting new patients'}`;
    return (
      <button
        key={idx}
        type="button"
        className={classes}
        aria-expanded={isExpanded}
        title={title}
        onClick={() => setExpandedKey(isExpanded ? null : key)}
      >
        {(slot.hasInPerson || slot.hasVideo) && (
          <span className="cal-slot-mode" aria-hidden="true">
            {slot.hasInPerson && <InPersonIcon size={16} />}
            {slot.hasVideo && <VideoVisitIcon size={16} />}
          </span>
        )}
        {slot.timeString}
        {slot.visitTypes.length > 0 && (
          <span className="cal-slot-caret" aria-hidden="true">{isExpanded ? '▴' : '▾'}</span>
        )}
      </button>
    );
  };

  // Render a group's slots, sub-grouped by department when the day spans several.
  const renderGroupSlots = (groupSlots: AvailabilityUniqueSlot[]) => {
    if (dayDeptCount <= 1) {
      return <div className="cal-slots">{groupSlots.map(renderSlotPill)}</div>;
    }
    const byDept = new Map<string, AvailabilityUniqueSlot[]>();
    groupSlots.forEach((s) => {
      const dept = s.departmentName || 'Location';
      const list = byDept.get(dept);
      if (list) list.push(s);
      else byDept.set(dept, [s]);
    });
    const deptEntries: Array<[string, AvailabilityUniqueSlot[]]> = [];
    byDept.forEach((deptSlots, dept) => deptEntries.push([dept, deptSlots]));
    return (
      <>
        {deptEntries.map(([dept, deptSlots]) => (
          <div key={dept} className="cal-dept">
            <div className="cal-dept-name">{dept}</div>
            <div className="cal-slots">{deptSlots.map(renderSlotPill)}</div>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="avail-calendar">
      <div className="cal-layout">
        {/* Left column: month navigation + day grid */}
        <div className="cal-left">
          <div className="cal-header">
            <button type="button" className="cal-nav" onClick={() => shiftMonth(-1)} disabled={!canPrev} aria-label="Previous month">
              &larr;
            </button>
            <div className="cal-title">
              {MONTHS[view.month]} {view.year}
            </div>
            <button type="button" className="cal-nav" onClick={() => shiftMonth(1)} disabled={!canNext} aria-label="Next month">
              &rarr;
            </button>
          </div>

          <div className="cal-summary">
            {slots.length} open slot{slots.length === 1 ? '' : 's'} across {totalDays} day
            {totalDays === 1 ? '' : 's'}
            {firstDate && lastDate ? ` (through ${new Date(`${lastDate}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })})` : ''}
          </div>

          <div className="cal-grid cal-weekdays">
            {WEEKDAYS.map((w) => (
              <div key={w} className="cal-weekday">{w}</div>
            ))}
          </div>
          <div className="cal-grid">
            {cells.map((cell, i) => {
              if (!cell) return <div key={i} className="cal-cell cal-cell-empty" />;
              const daySlots = slotsByDate.get(cell.date);
              const count = daySlots?.length ?? 0;
              const hasNew = daySlots?.some((s) => s.acceptsNew) ?? false;
              const hasExisting = daySlots?.some((s) => s.acceptsExisting && !s.acceptsNew) ?? false;
              const isSelected = cell.date === selectedDate;
              const isToday = cell.date === todayStr;
              const classes = [
                'cal-cell',
                count > 0 ? 'cal-cell-available' : 'cal-cell-none',
                isSelected ? 'cal-cell-selected' : '',
                isToday ? 'cal-cell-today' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={i}
                  type="button"
                  className={classes}
                  onClick={() => count > 0 && setSelectedDate(cell.date)}
                  disabled={count === 0}
                  title={count > 0 ? `${count} slot${count === 1 ? '' : 's'}` : undefined}
                >
                  <span className="cal-daynum">{cell.day}</span>
                  {count > 0 && (
                    <span className="cal-dots">
                      {hasNew && <span className="cal-dot cal-dot-new" />}
                      {hasExisting && <span className="cal-dot cal-dot-existing" />}
                      <span className="cal-count">{count}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="cal-legend">
            <span><span className="cal-dot cal-dot-new" /> New patient</span>
            <span><span className="cal-dot cal-dot-existing" /> Existing only</span>
            <span><InPersonIcon size={16} /> In-person</span>
            <span><VideoVisitIcon size={16} /> Video</span>
          </div>
        </div>

        {/* Right column: selected-day slots */}
        <div className="cal-right">
          {jumpedForward && firstDate && (
            <div className="cal-jump-note">
              No openings this month — jumped to <strong>{new Date(`${firstDate}T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>, the first available date ({firstDateLabel}).
            </div>
          )}

          {selectedDate ? (
            <div className="cal-day-detail">
              <div className="cal-day-title">
                {new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                })}
                <span className="cal-day-count">{selectedSlots.length} slot{selectedSlots.length === 1 ? '' : 's'}</span>
              </div>
              {timeOfDayGroups.map((group, gi) => (
                <div key={group.label || gi} className="cal-tod-group">
                  {group.label && (
                    <div className="cal-tod-label">
                      {group.label}
                      <span className="cal-tod-count">{group.slots.length}</span>
                    </div>
                  )}
                  {renderGroupSlots(group.slots)}
                </div>
              ))}
              {(() => {
                const expandedSlot = selectedSlots.find((s) => slotKey(s) === expandedKey);
                if (!expandedSlot) {
                  return (
                    <div className="cal-slot-hint">
                      Tip: click any time to see what can be booked in that slot.
                    </div>
                  );
                }
                // Reasons are what patients pick from, so they lead when present;
                // the visit-type name is the fallback for types that have none —
                // which is every type in this dataset. Color by patient type.
                const reasonMap = new Map<string, { text: string; isNew: boolean; isExisting: boolean }>();
                const namedOnly: SlotVisitType[] = [];
                expandedSlot.visitTypes.forEach((vt) => {
                  if (!vt.reasons || vt.reasons.length === 0) {
                    namedOnly.push(vt);
                    return;
                  }
                  vt.reasons.forEach((r) => {
                    const k = r.trim().toLowerCase();
                    let e = reasonMap.get(k);
                    if (!e) {
                      e = { text: r.trim(), isNew: false, isExisting: false };
                      reasonMap.set(k, e);
                    }
                    if (vt.accepts === 'New') e.isNew = true;
                    else if (vt.accepts === 'Existing') e.isExisting = true;
                  });
                });
                const reasonChips: Array<{ text: string; isNew: boolean; isExisting: boolean }> = [];
                reasonMap.forEach((r) => reasonChips.push(r));
                const hasChips = reasonChips.length > 0 || namedOnly.length > 0;
                return (
                  <div className="cal-slot-detail">
                    <div className="cal-slot-detail-head">
                      <span className="cal-slot-detail-time">
                        {expandedSlot.timeString}
                        {expandedSlot.departmentName ? ` · ${expandedSlot.departmentName}` : ''}
                      </span>
                      <button
                        type="button"
                        className="cal-slot-detail-close"
                        onClick={() => setExpandedKey(null)}
                        aria-label="Close visit-type details"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="cal-slot-detail-sub">Bookable visit types</div>
                    {hasChips ? (
                      <div className="cal-reason-list">
                        {reasonChips.map((r, i) => (
                          <span
                            key={`r${i}`}
                            className={`cal-reason-chip ${r.isNew ? 'new' : 'existing'}`}
                            title={r.isNew && r.isExisting ? 'New & existing patients' : r.isNew ? 'New patients' : 'Existing patients'}
                          >
                            {r.text}
                          </span>
                        ))}
                        {namedOnly.map((vt, i) => (
                          <span
                            key={`v${i}`}
                            className={`cal-reason-chip ${vt.accepts === 'Existing' ? 'existing' : 'new'}`}
                            title={vt.accepts === 'Existing' ? 'Existing patients' : 'New patients'}
                          >
                            {vt.name || `Visit type ${vt.id}`}
                            {vt.name ? <span className="cal-chip-id">{vt.id}</span> : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="cal-slot-detail-none">
                        No visit type was returned for this slot.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="cal-day-empty">Select a highlighted day to see appointment times.</div>
          )}

          {firstDate && selectedDate !== firstDate && (
            <button type="button" className="cal-jump-btn" onClick={jumpToFirst}>
              ⤴ Jump to first available ({firstDateLabel})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
